import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { ProgressSpinner } from "primereact/progressspinner";

import { useToast } from "../../../hooks/useToast";

import { stepsKeys, } from "../../../components/DocumentGeneration/types";
import StepsControl from "../../../components/DocumentGeneration/components/StepsControl/StepsControl";
import { extractErrorMessage } from "../../../helpers/extractErrorMessage";

import Terms from "./Terms";
import Preview from "./Preview";
import Payment from "./Payment";
import SignWell from "./SignWell";
import FormFile15620 from "./FormFile15620";
import { TSelectedService, TTransportType } from "../../../@types/global";
import { transportTypes } from "../../../config/config";
import FinishStep from "../../../components/DocumentGeneration/components/FinishStep/FinishStep";

import { PublicHttpService } from "../../../services/PublicHttpService";
import { requests } from "../../../services/http-common";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";


type PagerProps = {
    email?: string;
    price: number;
    uspsPrice: number;
    isPublic: boolean;
    isDataLoaded?: boolean;
    paymentEnabled: boolean;
    isPublicOpenAi: boolean;
    templateId: string | undefined;
    deliveryService: string;

}
const isFromPayment = sessionStorage.getItem('processing');

function FormWrapper15620(props: PagerProps) {
    const {
        email,
        price,
        uspsPrice,
        isPublic,
        templateId,
        isDataLoaded,
        paymentEnabled,
        isPublicOpenAi,
    } = props;
    const { show } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoaded, setLoaded] = useState(false);

    const [fileId, setFieldId] = useState(searchParams.get('fileId'));
    const [isUpdateMode, setUpdateMode] = useState(searchParams.has('fileId'));
    const [isLastStep, setIsLastStep] = useState<boolean>(Boolean(searchParams.get('rdn')));
    const [step, setStep] = useState(() => {
        
        if(isFromPayment) {
           return stepsKeys.delivery; 
        }
        return isLastStep ? stepsKeys.signature : stepsKeys.filling;
    });
    const [source, setSource] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [transportType, setTransportType] = useState<TTransportType>(transportTypes.FEDEX);
    const [clientSecret, setClientSecret] = useState<string>('');
    const [selectedService, selectService] = useState<TSelectedService>('paid');
    const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
    const [requestedEmail, setRequestedWithEmail] = useState<string>('');

    const isPaidSelected = selectedService === 'paid';


    const handleCloseSuccessPopup = useCallback(() => {
        setShowSuccessPopup(false);
        setRequestedWithEmail('');
    }, []);

    const submitHandler = useCallback((formData: any, sendByEmail: Object) => {
        setLoaded(true);
        setFormData({ ...formData });

        const isTransportTypeEmail = Object.keys(sendByEmail).length;
        let params = {
            templateId,
            deliveryService: transportType,
            ...sendByEmail,
            replacements: { ...formData, firstRecipientEmail: email }
        }

        if (isTransportTypeEmail) {
            if(sessionStorage.getItem('processing')) {
                const data = JSON.parse(sessionStorage.getItem('processing') || '{}');
                sessionStorage.removeItem("processing");
                params = {
                  ...params,
                  ...data
                }
            }
        } else {
            if(isFromPayment) {
               params = JSON.parse(sessionStorage.getItem('processing') || '{}');
            } else {
                sessionStorage.setItem('processing', JSON.stringify({
                    isRegular: false,
                    ...params
                }));                
            }
        }
        
        PublicHttpService.post(
            "/api/public/file83b/generate-pdf-corpora",
            { ...params }).then(res => {
                setSource(res.data);

                setFieldId(res.data.formId);

                if (isTransportTypeEmail) {
                    return;
                } 
                const params = JSON.parse(sessionStorage.getItem('processing') || '{}');
                params.replacements["file83bId"] = res.data.formId
                sessionStorage.setItem('processing', JSON.stringify({
                    isRegular: false,
                    ...params
                }));
                return requests.get(`/api/public/checkout/create-session/${res.data.fileUUID}`)
            }).then(res => {
                if (!isTransportTypeEmail) {
                    window.location.href = res.sessionUrl;
                } else {
                    setShowSuccessPopup(true);
                }
            }).catch((error: AxiosError<{ message: string }>) => {
                extractErrorMessage({
                    error,
                    show: show,
                });
            }).finally(() => {
                setLoaded(false);
            });

    }, [transportType]);

    const handlePreviousStep = useCallback((data: any) => {
        setFormData(data);
        setStep(stepsKeys.review);
    }, []);

    return (
        <div className="w-full h-full flex flex-column gap-4">
            {
                isDataLoaded ? (
                    <>
                        <div className="w-full flex flex-column flex-row align-items-center">
                            <StepsControl
                                step={step}                                
                                isPublic={isPublic}
                                paymentEnabled={paymentEnabled}
                                isPublicOpenAi={isPublicOpenAi}
                                {...(isLastStep ? {
                                    isFLowCompleted: true,
                                    afterEmbeddedPay: true
                                } : {})}
                            />
                        </div>

                        <div className="w-full h-full flex flex-column flex-row align-items-center">
                            {
                                step === stepsKeys.filling && (
                                    <FormFile15620 templateId={templateId as string}
                                        deliveryService={transportType}
                                        firstRecipientEmail={email as string}
                                        formData={formData}
                                        fileId={fileId}
                                        onDataSave={handlePreviousStep} />
                                )
                            }
                            {
                                step === stepsKeys.review && (
                                    <Preview
                                        formData={formData}
                                        handleNextStep={() => setStep(stepsKeys.terms)}
                                        handlePreviousStep={() => {
                                            setStep(stepsKeys.filling);
                                        }}
                                    />
                                )
                            }

                            {
                                step === stepsKeys.terms && (
                                    <Terms
                                        isSubmitted={isLoaded}
                                        handleNextStep={() => {
                                            setStep(stepsKeys.delivery);
                                        }}
                                        handlePreviousStep={() => setStep(stepsKeys.review)} />
                                )
                            }
                            {
                                (step === stepsKeys.delivery) && (
                                    <>
                                        <FinishStep
                                            isGlobalLoading={isLoaded}
                                            isPublic={isPublic}
                                            setStep={setStep}
                                            signAndSubmit={(email: string | undefined = undefined) => {
                                                const sendByEmail = !!email ? {
                                                    email,
                                                    isSendEmail: true
                                                } : {};
                                                setRequestedWithEmail(email || '');
                                                submitHandler({ ...formData }, sendByEmail);
                                            }}
                                            price={price}
                                            uspsPrice={uspsPrice}
                                            transportType={transportType}
                                            setTransportType={setTransportType}
                                            isPaidSelected={isPaidSelected}
                                            selectedService={selectedService}
                                            selectService={selectService}
                                            isPublicOpenAi={isPublicOpenAi}
                                        />
                                        <ConfirmDialog
                                            visible={showSuccessPopup}
                                            onHide={handleCloseSuccessPopup}
                                            message={`You will receive an email at ${requestedEmail} with your 83(b) election form attached.`}
                                            header="Success"
                                            icon="pi pi-check"
                                            acceptClassName="p-button-danger"
                                            footer={() => <Button label="OK" onClick={() => {
                                                handleCloseSuccessPopup();
                                            }} />}
                                        />
                                    </>

                                )
                            }
                            {
                                step === stepsKeys.pay && (
                                    <Payment clientSecret={clientSecret}
                                        handlePreviousStep={() => setStep(stepsKeys.filling)} />
                                )
                            }
                            {
                                (step === stepsKeys.signature) && (
                                    <SignWell embeddedUrl={source.embeddedSigningUrl} />
                                )
                            }
                        </div>
                    </>
                ) : (
                    <div className="w-full h-full flex flex-column justify-content-center align-items-center">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                    </div>
                )
            }

        </div>
    )
}

export default FormWrapper15620;